/**
 * Serialise a JSON-LD graph for a `<script type="application/ld+json">` block.
 *
 * Only `<` is escaped, which is all that is needed to stop CMS text closing the
 * script tag; JSON parsers read `\u003c` back as `<`, so the data survives intact.
 * Do not reach for an HTML sanitiser here — the contents of a JSON-LD block are raw
 * text, not HTML, so entity-escaping corrupts the values a consumer reads back
 * (`a < b` would arrive as `a &lt; b`).
 */
export const toJsonLd = (data: unknown) => JSON.stringify(data).replace(/</g, '\\u003c');

/**
 * The site-wide publisher entity.
 *
 * `_document.tsx` emits it as an `Organization` block on every page, and per-page
 * entities such as `BlogPosting` embed it as their `publisher`. Both come from this
 * one definition so the two blocks cannot drift, and the shared `@id` lets a consumer
 * that merges a page's JSON-LD treat them as one node rather than two organizations.
 * It is embedded rather than referenced by `@id` alone because consumers are not
 * required to merge separate `<script>` blocks, and an unresolved reference is silent.
 *
 * The URLs are deliberately hardcoded to production rather than built from `DOMAIN`:
 * this describes the company, not the deployment, so a preview or the China site
 * should not claim its own origin as Sensor Tower's website. The logo and postal
 * address in `_document.tsx` have always been absolute for the same reason.
 */
export const ORGANIZATION_ID = 'https://sensortower.com/#organization';

export const organization = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'Sensor Tower',
  url: 'https://sensortower.com',
  logo: {
    '@type': 'ImageObject',
    url: 'https://sensortower.com/images/logo.png',
  },
};
